/**
 * The fleet, counted for the dashboard's device charts.
 *
 * Pure functions over the managed device list and live presence, so the bucketing is under test
 * without a canvas. Each chart is a list of rows in display order; a chart with one series has one
 * value per row, the per-customer chart has two (online, offline).
 *
 * Two rules every chart here follows:
 *
 *   * **Ordinal scales keep their order** - check-in recency, memory size and agent version read
 *     top to bottom in their own order, never re-sorted by count. Nominal ones (operating system,
 *     customer) sort by count, largest first.
 *   * **A long tail folds into one row** rather than growing the chart or being dropped: a list
 *     silently missing its tail reads as those devices not existing.
 *
 * Every row carries the ids of the devices it counts, folded rows included, so a click on any bar
 * can list exactly the machines behind its number.
 */
import type { DeviceDto, DevicePresenceDto, HardwareInfoDto } from './contracts.gen.js';
import { isWindowsCaption } from './platform.js';

export interface BarRow {
  readonly label: string;
  readonly values: readonly number[];
  /** The devices this row counts, for the drill-down list. */
  readonly deviceIds: readonly number[];
  /** Where clicking the row goes, when there is a page for it. */
  readonly path?: string;
}

export interface DeviceAnalytics {
  readonly total: number;
  readonly online: number;
  readonly checkIn: readonly BarRow[];
  readonly os: readonly BarRow[];
  /** Two values per row: online, offline. */
  readonly customers: readonly BarRow[];
  readonly agents: readonly BarRow[];
  readonly memory: readonly BarRow[];
  /** Windows devices only - a NAS or router has no Windows 11 to be ready for. */
  readonly tpm: readonly BarRow[];
  readonly windowsCount: number;
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export function analyzeDevices(
  devices: readonly DeviceDto[],
  presence: readonly DevicePresenceDto[],
  customerNames: ReadonlyMap<number, string>,
  now: number = Date.now()
): DeviceAnalytics {
  const byId = new Map(presence.map(p => [p.deviceId, p]));
  const isOnline = (d: DeviceDto): boolean => byId.get(d.id)?.isOnline ?? false;
  const windows = devices.filter(d => isWindowsCaption(d.os?.caption));

  return {
    total: devices.length,
    online: devices.filter(isOnline).length,
    checkIn: checkInRows(devices, byId, now),
    os: foldTail(countBy(devices, d => osName(d.os?.caption)), 6, 'Other'),
    customers: customerRows(devices, isOnline, customerNames),
    agents: agentRows(devices),
    memory: ordered(countBy(devices, d => memoryBucket(d.hardware)), MEMORY_ORDER),
    tpm: ordered(countBy(windows, d => tpmBucket(d.hardware)), TPM_ORDER),
    windowsCount: windows.length
  };
}

// ---------------------------------------------------------------------

export const CHECK_IN_ORDER = [
  'Online now',
  'Within 24 hours',
  '1 to 7 days',
  '8 to 30 days',
  'Over 30 days',
  'Never seen'
] as const;

function checkInRows(
  devices: readonly DeviceDto[],
  presence: ReadonlyMap<number, DevicePresenceDto>,
  now: number
): BarRow[] {
  const ids = new Map<string, number[]>();
  for (const d of devices) {
    const p = presence.get(d.id) ?? d.presence;
    // Presence lives in the server's memory and is empty after a restart; the last inventory push is
    // the durable record of when the device was last heard from.
    const bucket = checkInBucket(p?.isOnline ?? false, p?.lastSeenUtc ?? d.lastInventoryUtc, now);
    ids.set(bucket, [...(ids.get(bucket) ?? []), d.id]);
  }
  // Every bucket is kept, empty or not: the shape of the whole scale is the point of this chart, and
  // an empty "Over 30 days" is the good news it exists to show.
  return CHECK_IN_ORDER.map(label => {
    const deviceIds = ids.get(label) ?? [];
    return { label, values: [deviceIds.length], deviceIds };
  });
}

export function checkInBucket(isOnline: boolean, lastSeenUtc: string | null | undefined, now: number): string {
  if (isOnline) return 'Online now';
  const seen = lastSeenUtc ? Date.parse(lastSeenUtc) : NaN;
  if (Number.isNaN(seen)) return 'Never seen';
  const age = now - seen;
  if (age < DAY) return 'Within 24 hours';
  if (age < 7 * DAY) return '1 to 7 days';
  if (age < 30 * DAY) return '8 to 30 days';
  return 'Over 30 days';
}

/** "Windows 11 Pro" rather than "Microsoft Windows 11 Pro"; an empty caption is "Unknown". */
export function osName(caption: string | null | undefined): string {
  const text = (caption ?? '').trim().replace(/^Microsoft\s+/i, '');
  return text.length > 0 ? text : 'Unknown';
}

function customerRows(
  devices: readonly DeviceDto[],
  isOnline: (d: DeviceDto) => boolean,
  names: ReadonlyMap<number, string>
): BarRow[] {
  const tally = new Map<number, { on: number; off: number; ids: number[] }>();
  for (const d of devices) {
    const t = tally.get(d.customerId) ?? { on: 0, off: 0, ids: [] };
    if (isOnline(d)) t.on++;
    else t.off++;
    t.ids.push(d.id);
    tally.set(d.customerId, t);
  }
  const rows = [...tally.entries()]
    .map(([id, t]) => ({
      label: names.get(id) ?? `Customer #${id}`,
      values: [t.on, t.off],
      deviceIds: t.ids,
      path: `/customers/${id}`
    }))
    .sort((a, b) => sum(b.values) - sum(a.values) || a.label.localeCompare(b.label));
  const keep = 8;
  if (rows.length <= keep) return rows;
  const rest = rows.slice(keep - 1);
  return [
    ...rows.slice(0, keep - 1),
    {
      label: `${rest.length} other customers`,
      values: [rest.reduce((n, r) => n + r.values[0]!, 0), rest.reduce((n, r) => n + r.values[1]!, 0)],
      deviceIds: rest.flatMap(r => r.deviceIds)
    }
  ];
}

function agentRows(devices: readonly DeviceDto[]): BarRow[] {
  const counts = countBy(devices, d => d.agentVersion?.trim() || 'Unknown');
  const known = counts.filter(r => r.label !== 'Unknown').sort((a, b) => compareVersions(b.label, a.label));
  const unknown = counts.find(r => r.label === 'Unknown');
  const keep = 6;
  const rows =
    known.length <= keep
      ? known
      : [...known.slice(0, keep - 1), merged(known.slice(keep - 1), 'Older')];
  return unknown ? [...rows, unknown] : rows;
}

const MEMORY_ORDER = ['Under 8 GB', '8 GB', '16 GB', '32 GB', '64 GB or more', 'Not reported'];

/**
 * The installed memory, as the size it was sold as.
 *
 * Windows reports the usable total, a little under the installed size (31.4 GB on a 32 GB machine),
 * so the boundaries sit between the sizes rather than on them.
 */
export function memoryBucket(h: HardwareInfoDto | null | undefined): string {
  const bytes = h?.ramBytes ?? 0;
  if (bytes <= 0) return 'Not reported';
  const gb = bytes / 1024 ** 3;
  if (gb < 7) return 'Under 8 GB';
  if (gb < 12) return '8 GB';
  if (gb < 24) return '16 GB';
  if (gb < 48) return '32 GB';
  return '64 GB or more';
}

const TPM_ORDER = ['TPM 2.0 ready', 'Disabled in firmware', 'Older TPM', 'No TPM', 'Not reported'];

/**
 * Windows 11's TPM requirement, per machine.
 *
 * "Not reported" is not "No TPM": the agent could not READ it (see `tpmState`), and calling that a
 * blocker would flag machines with a working TPM 2.0.
 */
export function tpmBucket(h: HardwareInfoDto | null | undefined): string {
  const t = h?.tpm;
  if (!t) return 'Not reported';
  if (!t.present) return 'No TPM';
  if (!(t.enabled && t.activated)) return 'Disabled in firmware';
  return /^\s*2/.test(t.specVersion ?? '') || !t.specVersion ? 'TPM 2.0 ready' : 'Older TPM';
}

// ---------------------------------------------------------------------
function countBy(devices: readonly DeviceDto[], key: (d: DeviceDto) => string): BarRow[] {
  const ids = new Map<string, number[]>();
  for (const d of devices) {
    const k = key(d);
    ids.set(k, [...(ids.get(k) ?? []), d.id]);
  }
  return [...ids.entries()]
    .map(([label, deviceIds]) => ({ label, values: [deviceIds.length], deviceIds }))
    .sort((a, b) => b.values[0]! - a.values[0]! || a.label.localeCompare(b.label));
}

/** The largest `keep - 1` rows and one row for the rest. */
function foldTail(rows: readonly BarRow[], keep: number, label: string): BarRow[] {
  if (rows.length <= keep) return [...rows];
  return [...rows.slice(0, keep - 1), merged(rows.slice(keep - 1), label)];
}

/** Single-series rows folded into one. */
function merged(rows: readonly BarRow[], label: string): BarRow {
  return { label, values: [sum(rows.map(r => r.values[0]!))], deviceIds: rows.flatMap(r => r.deviceIds) };
}

/** Rows in a fixed order, empty buckets left out. */
function ordered(rows: readonly BarRow[], order: readonly string[]): BarRow[] {
  return order.flatMap(label => rows.filter(r => r.label === label));
}

function sum(values: readonly number[]): number {
  return values.reduce((n, v) => n + v, 0);
}

/** Numeric by component: 1.10.0 is newer than 1.9.0. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(n => Number.parseInt(n, 10) || 0);
  const pb = b.split('.').map(n => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
